import { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import {
  HandHeart, Hammer, Award, Building2, GraduationCap, MessageCircle, CheckCircle2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { contactConfig } from "@/config/contact";
import { Seo } from "@/components/Seo";

type Project = {
  id: string;
  track: string;
  project_name: string;
  description: string | null;
  deliverable: string | null;
  target_roles: string | null;
  duration_days: number | null;
};

const TRACKS = [
  {
    slug: "infra_ppp",
    title: "Infrastructure & PPP",
    icon: Building2,
    oneLiner: "Learn real PPP and infrastructure project skills — DPRs, risk matrices, GIS mapping.",
    teasers: ["Mini DPR Exercise", "PPP Structuring Case Study"],
  },
  {
    slug: "career_counsel",
    title: "Career Counseling",
    icon: GraduationCap,
    oneLiner: "Learn to guide students professionally — build your own counseling case files.",
    teasers: ["Career Guidance Case Files", "Employer Outreach Log"],
  },
] as const;

const STAGES = [
  { name: "Stage 1", label: "Hand-held", desc: "Mentor actively guides every step." },
  { name: "Stage 2", label: "Semi-guided", desc: "Candidate drives, mentor checks in at milestones." },
  { name: "Stage 3", label: "Independent", desc: "Fully self-directed, reviewed at submission only." },
];

const FAQS = [
  { q: "Do I have to pay anything?", a: "No. Guidance and the project tracks are completely free." },
  { q: "Do I get paid?", a: "You earn only if someone you refer later enrolls in a paid SkillSTEM course or gets placed through SkillSTEM. Nothing is owed by the candidate to you directly." },
  { q: "What if I can't finish a stage?", a: "No penalty — you can pause. Stage 1 is time-boxed to keep things moving." },
  { q: "Is there a certificate?", a: "Yes, issued after Stage 3 is mentor-approved." },
];

const waHref = (source: string, trackSlug?: string) => {
  const base = contactConfig.whatsapp.replace(/\?.*$/, "");
  const msg = trackSlug
    ? `Hi! I'd like to join the we-help.in Ambassador Program (track: ${trackSlug}, source: ${source}).`
    : `Hi! I'd like to start as a we-help.in Ambassador (source: ${source}).`;
  return `${base}?text=${encodeURIComponent(msg)}`;
};

const Ambassador = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [openTrack, setOpenTrack] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await (supabase as any)
        .from("project_library")
        .select("*")
        .order("track", { ascending: true });
      if (data) setProjects(data);
    })();
  }, []);

  const byTrack = useMemo(() => {
    const map: Record<string, Project[]> = {};
    for (const p of projects) (map[p.track] ||= []).push(p);
    return map;
  }, [projects]);

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title="Ambassador Program — we-help.in"
        description="Mentor students for free, complete real staged projects, and get referred to SkillSTEM's employer network."
      />
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-gradient-to-b from-accent/50 to-background py-14 md:py-20">
          <div className="container max-w-4xl text-center">
            <Badge variant="secondary" className="mb-4">we-help.in Ambassador Program</Badge>
            <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
              Free Guidance You Give. Real Projects You Build. Jobs You Get Referred To.
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join as a we-help.in Ambassador — mentor students for free, complete real infrastructure
              and career-guidance projects, and get referred to SkillSTEM's employer network.
            </p>
            <Button asChild variant="hero" size="lg" className="min-h-[56px]">
              <a href={waHref("ambassador_page")} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-5 w-5" /> Start as an Ambassador
              </a>
            </Button>
          </div>
        </section>

        {/* How it works */}
        <section className="py-12">
          <div className="container max-w-5xl">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">How it works</h2>
            <div className="grid gap-4 md:grid-cols-3">
              {[
                { icon: HandHeart, title: "Guide someone, free", body: "Help a student or candidate with career guidance or a mini infra project — no fee involved." },
                { icon: Hammer, title: "Build your project portfolio", body: "Complete staged, mentor-reviewed projects (Stage 1 → 2 → 3)." },
                { icon: Award, title: "Get certified & referred", body: "Once Stage 3 is passed, get a certificate and a referral into the employer network." },
              ].map((s, i) => (
                <div key={s.title} className="rounded-2xl bg-card border border-border p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                      <s.icon className="h-5 w-5" />
                    </div>
                    <span className="text-sm font-semibold text-muted-foreground">Step {i + 1}</span>
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Choose your track */}
        <section className="py-12 bg-muted/30">
          <div className="container max-w-5xl">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">Choose your track</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {TRACKS.map((t) => (
                <div key={t.slug} className="rounded-2xl bg-card border border-border p-6 flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                      <t.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-semibold">{t.title}</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">{t.oneLiner}</p>
                  <ul className="space-y-1 mb-6 text-sm">
                    {t.teasers.map((p) => (
                      <li key={p} className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary" /> {p}
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant="outline"
                    className="mt-auto min-h-[48px]"
                    onClick={() => {
                      setOpenTrack(t.slug);
                      setTimeout(() => document.getElementById(`track-${t.slug}`)?.scrollIntoView({ behavior: "smooth" }), 50);
                    }}
                  >
                    View this track
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Track detail */}
        {openTrack && (
          <section id={`track-${openTrack}`} className="py-12">
            <div className="container max-w-6xl">
              {(() => {
                const t = TRACKS.find((x) => x.slug === openTrack)!;
                const rows = byTrack[openTrack] || [];
                return (
                  <>
                    <h2 className="text-2xl md:text-3xl font-bold mb-2">{t.title} — Projects</h2>
                    <p className="text-muted-foreground mb-6">Pick any project. Your mentor helps you scope and stage it.</p>

                    <div className="overflow-x-auto rounded-2xl border border-border bg-card mb-6">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left">
                          <tr>
                            <th className="p-3 font-semibold">Project</th>
                            <th className="p-3 font-semibold">What they do</th>
                            <th className="p-3 font-semibold">Deliverable</th>
                            <th className="p-3 font-semibold">Target roles</th>
                            <th className="p-3 font-semibold whitespace-nowrap">Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.id} className="border-t border-border align-top">
                              <td className="p-3 font-medium">{r.project_name}</td>
                              <td className="p-3 text-muted-foreground">{r.description}</td>
                              <td className="p-3 text-muted-foreground">{r.deliverable}</td>
                              <td className="p-3 text-muted-foreground">{r.target_roles}</td>
                              <td className="p-3 whitespace-nowrap">{r.duration_days} days</td>
                            </tr>
                          ))}
                          {rows.length === 0 && (
                            <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Loading projects…</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="grid gap-3 md:grid-cols-3 mb-6">
                      {STAGES.map((s) => (
                        <div key={s.name} className="rounded-xl border border-border bg-card p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge>{s.name}</Badge>
                            <span className="font-semibold">{s.label}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{s.desc}</p>
                        </div>
                      ))}
                    </div>

                    <Button asChild variant="hero" size="lg" className="min-h-[52px]">
                      <a href={waHref("ambassador_track", t.slug)} target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="mr-2 h-5 w-5" /> Apply for {t.title}
                      </a>
                    </Button>
                  </>
                );
              })()}
            </div>
          </section>
        )}

        {/* Readiness snapshot */}
        <section className="py-12 bg-muted/30">
          <div className="container max-w-3xl">
            <div className="rounded-2xl border border-border bg-card p-6 md:p-8 text-center">
              <h2 className="text-2xl font-bold mb-3">Readiness Snapshot</h2>
              <p className="text-muted-foreground">
                As you complete each stage, your mentor marks you as <strong>Project-ready</strong>,
                <strong> Needs more mentoring</strong>, or <strong>Strong communicator</strong> — simple, honest feedback tags.
                No complex scoring — just clear, practical signals.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12">
          <div className="container max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card px-4">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-14 bg-gradient-to-b from-background to-accent/40">
          <div className="container max-w-3xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to start?</h2>
            <Button asChild variant="hero" size="lg" className="min-h-[56px]">
              <a href={waHref("ambassador_footer")} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-5 w-5" /> Start as an Ambassador
              </a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Ambassador;